import React, { useCallback, useEffect, useMemo, useState } from "react";

import Button from "../../../Components/Button";
import DropdownSelector from "../../../Components/DropdownSelector";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchEngagementConfig,
  updateEngagementConfig,
} from "../../../Store/Reducers/SponsorEngagementSlice";
import { DEFAULT_SPONSOR_ENGAGEMENT_PROGRAM } from "../../../Utils/Constants/sponsorEngagementDefaults";
import {
  CYCLE_CADENCE_OPTIONS,
  poll1FrequencyFromConfig,
  poll2FrequencyFromConfig,
} from "../../../Utils/Helper/sponsorEngagementLaunch";
import {
  CFG_ACTIONS,
  CFG_CONTROL_ROW,
  CFG_EVIDENCE_SUBHDR,
  CFG_EVIDENCE_TABLE,
  CFG_PAGE_HEADER,
  CFG_PAGE_SUB,
  CFG_PAGE_TITLE,
  CFG_QMETA,
  CFG_QTEXT,
  CFG_SECTION,
  CFG_SECTION_H,
  CFG_SECTION_SUB,
  CFG_TIER_MAP_TABLE,
  CFG_TIER_TABLE,
  CFG_WEIGHT_LBL,
  CFG_WEIGHT_ROW,
  CFG_WEIGHT_SLIDER,
  CFG_WEIGHT_SUM,
  CFG_WEIGHT_SUM_WARN,
  CFG_WEIGHT_VAL,
  MCI_MUTED,
  tierDotClass,
  tierPillClass,
} from "../../../Utils/Helper/sponsorEngagementUi";
import { toast } from "../../../Utils/Helper/toast";

type WeightKey = "health" | "arr" | "team" | "activity";

const WEIGHT_DEFS: { key: WeightKey; label: string }[] = [
  { key: "team", label: "Team Feedback (Poll 2)" },
  { key: "activity", label: "Engagement Activity (Poll 1)" },
];

const TIER_MAP_COLGROUP = (
  <colgroup>
    <col style={{ width: "33.33%" }} />
    <col style={{ width: "33.33%" }} />
    <col style={{ width: "33.33%" }} />
  </colgroup>
);

const TIER_MAP_TABLE_HEAD = (
  <thead>
    <tr>
      <th>Raw Range</th>
      <th>5-pt Score</th>
      <th>Tier Label</th>
    </tr>
  </thead>
);

const VISIBLE_WEIGHT_KEYS = WEIGHT_DEFS.map(w => w.key);

const defaultLeaderboardWeights = () =>
  DEFAULT_SPONSOR_ENGAGEMENT_PROGRAM.scoring_config.leaderboard_weights;

/** Team + Activity must sum to 100% in the UI; health/arr stay for backend composite. */
const normalizeVisibleLeaderboardWeights = (
  weights: Record<string, number | undefined>
) => {
  const fallback = defaultLeaderboardWeights();
  const health = Number(weights.health ?? fallback.health ?? 0.25);
  const arr = Number(weights.arr ?? fallback.arr ?? 0.25);
  let team = Number(weights.team ?? 0.5);
  let activity = Number(weights.activity ?? 0.5);
  const visibleSum = team + activity;
  if (visibleSum <= 0.005) {
    team = 0.5;
    activity = 0.5;
  } else if (Math.abs(visibleSum - 1) > 0.005) {
    team = team / visibleSum;
    activity = activity / visibleSum;
  }
  return { health, arr, team, activity };
};

const cloneConfig = (cfg: Record<string, any>) =>
  JSON.parse(JSON.stringify(cfg)) as Record<string, any>;

const withNormalizedLeaderboardWeights = (cfg: Record<string, any>) => {
  const next = cloneConfig(cfg);
  const current = next.scoring_config?.leaderboard_weights ?? {};
  next.scoring_config = {
    ...next.scoring_config,
    leaderboard_weights: normalizeVisibleLeaderboardWeights(current),
  };
  return next;
};

/** Compare only fields that are persisted on save (ignore poll question metadata). */
const persistSnapshot = (cfg: Record<string, any>) => ({
  cycle_config: cfg.cycle_config,
  scoring_config: cfg.scoring_config,
  poll1_evidence_config: cfg.poll1_evidence_config,
  poll2_evidence_config: cfg.poll2_evidence_config,
});

const TierPill = ({ tier }: { tier: string }) => (
  <span className={tierPillClass(tier)}>
    <span className={tierDotClass(tier)} />
    {tier}
  </span>
);

const SettingsNativeReplacement: React.FC<{
  label: string;
  selected: string;
  options: { label: string; value: string }[];
  onSelect: (value: string) => void;
}> = ({ label, selected, options, onSelect }) => (
  <div className="max-w-[280px] w-full">
    <DropdownSelector
      label={label}
      selected={selected}
      options={options}
      onSelect={onSelect}
      triggerStyle="field"
      enableScroll
      fullWidth
      hideLabel
    />
  </div>
);

const SponsorEngagementProgram: React.FC = () => {
  const dispatch = useAppDispatch();
  const configState = useAppSelector(s => s.sponsorEngagement.config);
  const saving = useAppSelector(s => s.sponsorEngagement.updateConfig.loading);
  const [config, setConfig] = useState<Record<string, any> | null>(null);
  const [saved, setSaved] = useState<Record<string, any> | null>(null);
  const loading = configState.loading && !config;

  const load = useCallback(async () => {
    const result = await dispatch(fetchEngagementConfig());
    if (fetchEngagementConfig.fulfilled.match(result)) {
      const normalized = withNormalizedLeaderboardWeights(
        cloneConfig(result.payload)
      );
      setConfig(normalized);
      setSaved(cloneConfig(normalized));
    } else {
      toast.error(
        (result.payload as string) || "Failed to load program settings"
      );
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const scoring = config?.scoring_config ?? {};
  const weights = scoring.leaderboard_weights ?? {};

  const setPoll1Frequency = (poll1_frequency: string) => {
    setConfig(prev => {
      if (!prev) return prev;
      const next = cloneConfig(prev);
      next.cycle_config = {
        ...(next.cycle_config ?? {}),
        poll1_frequency,
        cadence: poll1_frequency,
      };
      return next;
    });
  };

  const setPoll2Frequency = (poll2_frequency: string) => {
    setConfig(prev => {
      if (!prev) return prev;
      const next = cloneConfig(prev);
      next.cycle_config = { ...(next.cycle_config ?? {}), poll2_frequency };
      return next;
    });
  };

  const weightSumPct = useMemo(
    () =>
      VISIBLE_WEIGHT_KEYS.reduce(
        (sum, key) => sum + Number(weights[key] ?? 0) * 100,
        0
      ),
    [weights]
  );

  const weightSumWarn = Math.abs(weightSumPct - 100) > 0.5;

  const dirty = useMemo(() => {
    if (!config || !saved) return false;
    return (
      JSON.stringify(persistSnapshot(config)) !==
      JSON.stringify(persistSnapshot(saved))
    );
  }, [config, saved]);

  const setWeightPct = (key: WeightKey, pct: number) => {
    if (!VISIBLE_WEIGHT_KEYS.includes(key)) return;
    const clamped = Math.min(100, Math.max(0, pct));
    const otherKey = key === "team" ? "activity" : "team";
    setConfig(prev => {
      if (!prev) return prev;
      const next = cloneConfig(prev);
      const existing = next.scoring_config?.leaderboard_weights ?? {};
      next.scoring_config = {
        ...next.scoring_config,
        leaderboard_weights: {
          ...existing,
          [key]: clamped / 100,
          [otherKey]: (100 - clamped) / 100,
        },
      };
      return next;
    });
  };

  const setScoringField = (field: string, value: number | boolean) => {
    setConfig(prev => {
      if (!prev) return prev;
      const next = cloneConfig(prev);
      next.scoring_config = { ...next.scoring_config, [field]: value };
      return next;
    });
  };

  const setTierBound = (
    table: "poll1_tiers" | "poll2_tiers" | "portfolio_tiers",
    index: number,
    field: "min" | "max",
    value: number
  ) => {
    setConfig(prev => {
      if (!prev) return prev;
      const next = cloneConfig(prev);
      const tiers = [...(next.scoring_config?.[table] ?? [])];
      tiers[index] = { ...tiers[index], [field]: value };
      next.scoring_config = { ...next.scoring_config, [table]: tiers };
      return next;
    });
  };

  const setPoll1Evidence = (
    id: string,
    patch: { evidence_type?: string; evidence_required?: boolean }
  ) => {
    setConfig(prev => {
      if (!prev) return prev;
      const next = cloneConfig(prev);
      const list = [...(next.poll1_evidence_config ?? [])];
      const idx = list.findIndex((r: any) => r.id === id);
      if (idx < 0) return prev;
      const updated = { ...list[idx], ...patch };
      if (patch.evidence_type === "none") {
        updated.evidence_required = false;
      }
      list[idx] = updated;
      next.poll1_evidence_config = list;
      return next;
    });
  };

  const setPoll2EvidenceType = (id: string, evidenceType: string) => {
    setConfig(prev => {
      if (!prev) return prev;
      const next = cloneConfig(prev);
      const list = [...(next.poll2_evidence_config ?? [])];
      const idx = list.findIndex((r: any) => r.id === id);
      if (idx < 0) return prev;
      list[idx] = { ...list[idx], evidence_type: evidenceType };
      next.poll2_evidence_config = list;
      return next;
    });
  };

  const handleSave = async () => {
    if (!config) return;
    if (weightSumWarn) {
      toast.error(
        "Team Feedback and Engagement Activity weights must sum to 100%"
      );
      return;
    }
    const payload = {
      cycle_config: config.cycle_config,
      scoring_config: config.scoring_config,
      poll1_evidence_config: config.poll1_evidence_config,
      poll2_evidence_config: config.poll2_evidence_config,
    };
    const result = await dispatch(updateEngagementConfig(payload));
    if (updateEngagementConfig.fulfilled.match(result)) {
      const normalized = cloneConfig(result.payload);
      setConfig(normalized);
      setSaved(cloneConfig(normalized));
      toast.success("Configuration saved · all views re-derived");
    } else {
      toast.error((result.payload as string) || "Save failed");
    }
  };

  const handleRestoreDefaults = () => {
    if (!config) return;
    const defaults = cloneConfig(DEFAULT_SPONSOR_ENGAGEMENT_PROGRAM);
    const restored = withNormalizedLeaderboardWeights({
      ...config,
      cycle_config: defaults.cycle_config,
      scoring_config: defaults.scoring_config,
      poll1_evidence_config: defaults.poll1_evidence_config,
      poll2_evidence_config: defaults.poll2_evidence_config,
    });
    setConfig(restored);
    toast.success("Defaults restored — save to persist");
  };

  const poll1Questions: any[] = config?.poll1_questions ?? [];
  const poll2Dimensions: any[] = config?.poll2_dimensions ?? [];
  const poll1Evidence: any[] = config?.poll1_evidence_config ?? [];
  const poll2Evidence: any[] = config?.poll2_evidence_config ?? [];

  const poll1EvidenceById = useMemo(
    () => Object.fromEntries(poll1Evidence.map((e: any) => [e.id, e])),
    [poll1Evidence]
  );

  const poll2EvidenceById = useMemo(
    () => Object.fromEntries(poll2Evidence.map((e: any) => [e.id, e])),
    [poll2Evidence]
  );

  if (loading) {
    return (
      <div className="settings-section bg-white p-6 rounded-lg shadow border border-gray-200">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="settings-section bg-white p-6 rounded-lg shadow border border-gray-200">
        <p className="text-gray-500 text-sm">
          Unable to load program configuration.
        </p>
      </div>
    );
  }

  return (
    <div className="settings-section bg-white p-6 rounded-lg shadow border border-gray-200">
      <div className={CFG_PAGE_HEADER}>
        <div>
          <h2 className={CFG_PAGE_TITLE}>Sponsor Engagement Discovery</h2>
          <p className={CFG_PAGE_SUB}>
            Scoring weights, tier thresholds, and operational knobs. Changes
            re-derive all views instantly.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-0">
        <div className={CFG_SECTION}>
          <div className={CFG_SECTION_H}>Cycle Configuration</div>
          <div className={CFG_SECTION_SUB}>
            Defines how Discovery Hub resolves active poll periods and due-date
            limits. Poll 1 and Poll 2 can run on independent cadences; ESL users
            choose target audience per launch.
          </div>
          <div className={CFG_CONTROL_ROW}>
            <label htmlFor="poll1-frequency">Poll 1 Frequency</label>
            <SettingsNativeReplacement
              label="Poll 1 Frequency"
              selected={poll1FrequencyFromConfig(config)}
              options={CYCLE_CADENCE_OPTIONS}
              onSelect={setPoll1Frequency}
            />
          </div>
          <div className={CFG_CONTROL_ROW}>
            <label htmlFor="poll2-frequency">Poll 2 Frequency</label>
            <SettingsNativeReplacement
              label="Poll 2 Frequency"
              selected={poll2FrequencyFromConfig(config)}
              options={CYCLE_CADENCE_OPTIONS}
              onSelect={setPoll2Frequency}
            />
          </div>
        </div>

        <div className={CFG_SECTION}>
          <div className={CFG_SECTION_H}>Late Window Poll Configuration</div>
          <div className={CFG_SECTION_SUB}>
            Number of days after the due date that incomplete participants may
            still submit responses. When this period ends, the poll moves to
            Completed Polls and non-responders are marked as not responded.
          </div>
          <div className={CFG_CONTROL_ROW}>
            <label htmlFor="late-window-days">Late window (days)</label>
            <input
              id="late-window-days"
              type="number"
              min={0}
              max={90}
              value={scoring.late_window_grace_days ?? 14}
              onChange={e =>
                setScoringField(
                  "late_window_grace_days",
                  Math.max(0, Number(e.target.value) || 0)
                )
              }
            />
          </div>
        </div>

        <div className={CFG_SECTION}>
          <div className={CFG_SECTION_H}>Leaderboard Composite Weights</div>
          <div className={CFG_SECTION_SUB}>
            Weighted average across Team Feedback (Poll 2) and Engagement
            Activity (Poll 1). Adjust how much each factor contributes to a
            sponsor&apos;s composite. The leaderboard re-ranks immediately.
          </div>
          {WEIGHT_DEFS.map(({ key, label }) => {
            const pct = Math.round(Number(weights[key] ?? 0) * 100);
            return (
              <div key={key} className={CFG_WEIGHT_ROW}>
                <div className={CFG_WEIGHT_LBL}>{label}</div>
                <input
                  type="range"
                  className={CFG_WEIGHT_SLIDER}
                  min={0}
                  max={100}
                  step={5}
                  value={pct}
                  onChange={e => setWeightPct(key, Number(e.target.value))}
                />
                <div className={CFG_WEIGHT_VAL}>{pct}%</div>
              </div>
            );
          })}
          <div
            className={`${CFG_WEIGHT_SUM} ${weightSumWarn ? CFG_WEIGHT_SUM_WARN : ""}`}
          >
            <span>Sum of weights</span>
            <span>
              {weightSumPct.toFixed(0)}%{" "}
              {weightSumWarn ? "· Must equal 100%" : "· OK"}
            </span>
          </div>
        </div>

        <div className={CFG_SECTION}>
          <div className={CFG_SECTION_H}>
            Poll 1 — Sponsor Self-Report Tier Map
          </div>
          <div className={CFG_SECTION_SUB}>
            Maps raw point sum (0–8) to 5-point scale and to a tier label. Edit
            the boundaries to match your engagement model.
          </div>
          <table className={CFG_TIER_MAP_TABLE}>
            {TIER_MAP_COLGROUP}
            {TIER_MAP_TABLE_HEAD}
            <tbody>
              {(scoring.poll1_tiers ?? []).map((row: any, i: number) => (
                <tr key={`p1-${i}`}>
                  <td>
                    <input
                      type="number"
                      value={row.min}
                      onChange={e =>
                        setTierBound(
                          "poll1_tiers",
                          i,
                          "min",
                          Number(e.target.value)
                        )
                      }
                    />{" "}
                    –{" "}
                    <input
                      type="number"
                      value={row.max}
                      onChange={e =>
                        setTierBound(
                          "poll1_tiers",
                          i,
                          "max",
                          Number(e.target.value)
                        )
                      }
                    />
                  </td>
                  <td>{row.score_5pt}</td>
                  <td>
                    <TierPill tier={row.tier} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={CFG_SECTION}>
          <div className={CFG_SECTION_H}>Poll 2 — Team Feedback Tier Map</div>
          <div className={CFG_SECTION_SUB}>
            Maps the sum of 3 rating dimensions (3–15) to a 5-point scale.
          </div>
          <table className={CFG_TIER_MAP_TABLE}>
            {TIER_MAP_COLGROUP}
            {TIER_MAP_TABLE_HEAD}
            <tbody>
              {(scoring.poll2_tiers ?? []).map((row: any, i: number) => (
                <tr key={`p2-${i}`}>
                  <td>
                    <input
                      type="number"
                      value={row.min}
                      onChange={e =>
                        setTierBound(
                          "poll2_tiers",
                          i,
                          "min",
                          Number(e.target.value)
                        )
                      }
                    />{" "}
                    –{" "}
                    <input
                      type="number"
                      value={row.max}
                      onChange={e =>
                        setTierBound(
                          "poll2_tiers",
                          i,
                          "max",
                          Number(e.target.value)
                        )
                      }
                    />
                  </td>
                  <td>{row.score_5pt}</td>
                  <td>{row.label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={CFG_SECTION}>
          <div className={CFG_SECTION_H}>Portfolio Tier Boundaries</div>
          <div className={CFG_SECTION_SUB}>
            Composite ranges that determine the sponsor-level tier shown on the
            leaderboard.
          </div>
          <table className={CFG_TIER_TABLE}>
            <thead>
              <tr>
                <th>Composite Range</th>
                <th>Portfolio Tier</th>
              </tr>
            </thead>
            <tbody>
              {(scoring.portfolio_tiers ?? []).map((row: any, i: number) => (
                <tr key={`port-${i}`}>
                  <td>
                    <input
                      type="number"
                      step={0.1}
                      value={row.min}
                      onChange={e =>
                        setTierBound(
                          "portfolio_tiers",
                          i,
                          "min",
                          Number(e.target.value)
                        )
                      }
                    />{" "}
                    –{" "}
                    <input
                      type="number"
                      step={0.1}
                      value={row.max}
                      onChange={e =>
                        setTierBound(
                          "portfolio_tiers",
                          i,
                          "max",
                          Number(e.target.value)
                        )
                      }
                    />
                  </td>
                  <td>
                    <TierPill tier={row.tier} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={CFG_SECTION}>
          <div className={CFG_SECTION_H}>Operational Knobs</div>
          <div className={CFG_SECTION_SUB}>
            Streak threshold, nudge timing, and other workflow defaults.
          </div>
          <div className={CFG_CONTROL_ROW}>
            <label htmlFor="streak-threshold">Streak Threshold</label>
            <SettingsNativeReplacement
              label="Streak Threshold"
              selected={String(scoring.streak_threshold ?? 3)}
              options={[2, 3, 4, 5].map(n => ({
                label: `${n} quarters`,
                value: String(n),
              }))}
              onSelect={value =>
                setScoringField("streak_threshold", Number(value))
              }
            />
          </div>
          <div className={CFG_CONTROL_ROW}>
            <label htmlFor="nudge-weeks">Mid-Quarter Nudge Timing</label>
            <SettingsNativeReplacement
              label="Mid-Quarter Nudge Timing"
              selected={String(scoring.nudge_weeks_before ?? 6)}
              options={[4, 5, 6, 7, 8].map(n => ({
                label: `${n} weeks before due date`,
                value: String(n),
              }))}
              onSelect={value =>
                setScoringField("nudge_weeks_before", Number(value))
              }
            />
          </div>
          <div className={CFG_CONTROL_ROW}>
            <label htmlFor="rubric-toggle">
              Show Scoring Rubric to Sponsors (Poll 1)
            </label>
            <SettingsNativeReplacement
              label="Show Scoring Rubric to Sponsors (Poll 1)"
              selected={
                scoring.show_rubric_to_sponsors !== false ? "true" : "false"
              }
              options={[
                {
                  value: "true",
                  label: "Show — full transparency on points per question",
                },
                {
                  value: "false",
                  label: "Hide — discourage gaming; sponsors see only progress",
                },
              ]}
              onSelect={value =>
                setScoringField("show_rubric_to_sponsors", value === "true")
              }
            />
          </div>
        </div>

        <div className={CFG_SECTION}>
          <div className={CFG_SECTION_H}>Question Evidence Configuration</div>
          <div className={CFG_SECTION_SUB}>
            Choose the evidence prompt shown under each Poll 1 question
            (rendered only when sponsor answers Yes) and each Poll 2 dimension.
            Set required on Poll 1 questions to block submission until evidence
            is provided. Poll 2 evidence is always optional in this phase.
          </div>

          <div className={CFG_EVIDENCE_SUBHDR}>
            Poll 1 — Sponsor Self-Report
          </div>
          <table className={CFG_EVIDENCE_TABLE}>
            <thead>
              <tr>
                <th>Question</th>
                <th>Evidence Type</th>
                <th className="center">
                  Required
                  <br />
                  (on Yes)
                </th>
              </tr>
            </thead>
            <tbody>
              {poll1Questions.map((q: any) => {
                const ev = poll1EvidenceById[q.id] ?? {};
                const evType = ev.evidence_type ?? "none";
                return (
                  <tr key={q.id}>
                    <td>
                      <div className={CFG_QTEXT}>
                        {q.id.toUpperCase()}. {q.text}
                      </div>
                      <div className={CFG_QMETA}>
                        Window: {q.window} · {q.pts}{" "}
                        {q.pts === 1 ? "pt" : "pts"} on Yes
                      </div>
                    </td>
                    <td>
                      <SettingsNativeReplacement
                        label={`${q.id} evidence type`}
                        selected={evType}
                        options={["none", "text", "date", "link"].map(t => ({
                          value: t,
                          label: t.charAt(0).toUpperCase() + t.slice(1),
                        }))}
                        onSelect={value =>
                          setPoll1Evidence(q.id, {
                            evidence_type: value,
                          })
                        }
                      />
                    </td>
                    <td className="center">
                      <input
                        type="checkbox"
                        checked={Boolean(ev.evidence_required)}
                        disabled={evType === "none"}
                        onChange={e =>
                          setPoll1Evidence(q.id, {
                            evidence_required: e.target.checked,
                          })
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className={CFG_EVIDENCE_SUBHDR}>
            Poll 2 — Account Team Feedback
          </div>
          <table className={CFG_EVIDENCE_TABLE}>
            <thead>
              <tr>
                <th>Dimension</th>
                <th>Evidence Type</th>
                <th className="center">Required</th>
              </tr>
            </thead>
            <tbody>
              {poll2Dimensions.map((d: any) => {
                const ev = poll2EvidenceById[d.id] ?? {};
                return (
                  <tr key={d.id}>
                    <td>
                      <div className={CFG_QTEXT}>{d.title}</div>
                      <div className={CFG_QMETA}>{d.desc}</div>
                    </td>
                    <td>
                      <SettingsNativeReplacement
                        label={`${d.id} evidence type`}
                        selected={ev.evidence_type ?? "text"}
                        options={["none", "text"].map(t => ({
                          value: t,
                          label: t.charAt(0).toUpperCase() + t.slice(1),
                        }))}
                        onSelect={value => setPoll2EvidenceType(d.id, value)}
                      />
                    </td>
                    <td className="center">
                      <span className={MCI_MUTED}>
                        Always optional (Phase 1)
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={CFG_ACTIONS}>
          <Button
            variant="secondary"
            onClick={handleRestoreDefaults}
            disabled={saving}
          >
            Restore Defaults
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!dirty || saving}
            loading={saving}
          >
            Save Configuration
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SponsorEngagementProgram;
